import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Alert, Modal } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuth from '../dashboard/useAuth';
import 'react-phone-number-input/style.css'
import PhoneInput from 'react-phone-number-input'
import Select from "react-select";
import { FaHandshake, FaSave, FaTag, FaFileContract, FaCheckCircle } from 'react-icons/fa';
import axios from 'axios';
import { getAuthToken } from '../../authToken';

// ========== FONCTIONS DE DETECTION DE PAYS ==========
const detectCountryByGeolocation = () => {
    console.log('📍 [Géolocalisation] Démarrage de la détection GPS...');
    return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
            console.error('❌ [Géolocalisation] Navigateur ne supporte pas la géolocalisation');
            reject(new Error('La géolocalisation n\'est pas supportée par ce navigateur'));
            return;
        }

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                console.log('📍 [Géolocalisation] Position obtenue:', position.coords.latitude, position.coords.longitude);
                const { latitude, longitude } = position.coords;

                try {
                    const response = await fetch(
                        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
                        {
                            headers: {
                                'User-Agent': 'InovactApp/1.0'
                            }
                        }
                    );

                    if (!response.ok) {
                        throw new Error('Erreur lors de la récupération du pays');
                    }

                    const data = await response.json();
                    const countryCode = data.address?.country_code?.toUpperCase();
                    console.log('📍 [Géolocalisation] Pays détecté par GPS:', countryCode);

                    const countryMapping = {
                        'BJ': 'BJ', 'FR': 'FR', 'CI': 'CI', 'SN': 'SN', 'CM': 'CM',
                        'TG': 'TG', 'NG': 'NG', 'GH': 'GH', 'US': 'US', 'CA': 'CA',
                        'GB': 'GB', 'DE': 'DE', 'BE': 'BE', 'CH': 'CH', 'LU': 'LU',
                        'MA': 'MA', 'TN': 'TN', 'DZ': 'DZ', 'BF': 'BF', 'ML': 'ML',
                        'NE': 'NE', 'TD': 'TD', 'GA': 'GA', 'CG': 'CG', 'CD': 'CD'
                    };

                    const detectedCountry = countryMapping[countryCode] || 'BJ';
                    console.log('✅ [Géolocalisation] Pays final:', detectedCountry);
                    resolve(detectedCountry);

                } catch (error) {
                    console.error('❌ [Géolocalisation] Erreur API:', error);
                    reject(error);
                }
            },
            (error) => {
                console.error('❌ [Géolocalisation] Erreur:', error.message);
                let errorMessage = '';
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        errorMessage = 'Permission de géolocalisation refusée';
                        break;
                    case error.POSITION_UNAVAILABLE:
                        errorMessage = 'Position non disponible';
                        break;
                    case error.TIMEOUT:
                        errorMessage = 'Délai de géolocalisation dépassé';
                        break;
                    default:
                        errorMessage = 'Erreur de géolocalisation';
                }
                reject(new Error(errorMessage));
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0
            }
        );
    });
};

const detectCountryByIP = async () => {
    console.log('🌐 [IP] Démarrage de la détection par IP...');
    try {
        const response = await fetch('https://ipapi.co/json/');
        if (!response.ok) {
            throw new Error('Erreur lors de la récupération du pays');
        }
        const data = await response.json();
        const countryCode = data.country_code;
        console.log('🌐 [IP] Pays détecté par IP:', countryCode, 'Pays:', data.country_name);

        const supportedCountries = ['BJ', 'FR', 'CI', 'SN', 'CM', 'TG', 'NG', 'GH',
            'US', 'CA', 'GB', 'DE', 'BE', 'CH', 'LU', 'MA',
            'TN', 'DZ', 'BF', 'ML', 'NE', 'TD', 'GA', 'CG', 'CD'];

        const result = supportedCountries.includes(countryCode) ? countryCode : 'BJ';
        console.log('✅ [IP] Pays final:', result);
        return result;
    } catch (error) {
        console.error('❌ [IP] Erreur:', error);
        return 'BJ';
    }
};

const detectCountryAdaptive = async () => {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    console.log(`🖥️ [Adaptatif] Appareil détecté: ${isMobile ? 'MOBILE' : 'PC'}`);

    // Sur mobile, privilégier la géolocalisation
    if (isMobile) {
        console.log('📱 [Mobile] Tentative de géolocalisation GPS...');
        try {
            const geoCountry = await detectCountryByGeolocation();
            console.log('✅ [Mobile] Succès GPS:', geoCountry);
            return { country: geoCountry, method: 'Géolocalisation GPS', isMobile };
        } catch (error) {
            console.log('⚠️ [Mobile] GPS échoué, fallback IP');
            const ipCountry = await detectCountryByIP();
            console.log('✅ [Mobile] Fallback IP réussi:', ipCountry);
            return { country: ipCountry, method: 'Détection IP (fallback)', isMobile };
        }
    }
    // Sur PC, privilégier l'IP
    else {
        console.log('💻 [PC] Tentative de détection IP...');
        try {
            const ipCountry = await detectCountryByIP();
            console.log('✅ [PC] IP réussi:', ipCountry);
            return { country: ipCountry, method: 'Détection IP', isMobile };
        } catch (error) {
            console.log('⚠️ [PC] IP échouée, fallback géolocalisation');
            try {
                const geoCountry = await detectCountryByGeolocation();
                console.log('✅ [PC] Fallback GPS réussi:', geoCountry);
                return { country: geoCountry, method: 'Géolocalisation WiFi', isMobile };
            } catch {
                console.log('⚠️ [PC] Fallback échoué, utilisation Bénin par défaut');
                return { country: 'BJ', method: 'Valeur par défaut', isMobile };
            }
        }
    }
};

const useCountryDetection = () => {
    console.log('🚀 [Hook] Initialisation du hook de détection');
    const [country, setCountry] = useState('BJ');
    const [isDetecting, setIsDetecting] = useState(true);
    const [detectionMethod, setDetectionMethod] = useState('');
    const [isMobile, setIsMobile] = useState(false);
    const [detectionError, setDetectionError] = useState(null);

    const reDetect = async () => {
        console.log('🔄 [Hook] Re-détection manuelle demandée');
        setIsDetecting(true);
        setDetectionError(null);

        try {
            const result = await detectCountryAdaptive();
            console.log('✅ [Hook] Re-détection réussie:', result);
            setCountry(result.country);
            setDetectionMethod(result.method);
            setIsMobile(result.isMobile);

            if (result.country === 'BJ' && result.method === 'Valeur par défaut') {
                setDetectionError('Détection automatique impossible, utilisation du Bénin par défaut');
            }
            return result;
        } catch (error) {
            console.error('❌ [Hook] Échec re-détection:', error);
            setDetectionError('Impossible de détecter votre pays automatiquement');
            setCountry('BJ');
            setDetectionMethod('Valeur par défaut');
            return null;
        } finally {
            setIsDetecting(false);
        }
    };

    useEffect(() => {
        console.log('🎯 [Hook] useEffect déclenché - Début détection');
        const detectCountry = async () => {
            console.log('🔄 [Hook] Lancement de la détection...');
            setIsDetecting(true);
            setDetectionError(null);

            try {
                const result = await detectCountryAdaptive();
                console.log('✅ [Hook] Détection réussie!', {
                    pays: result.country,
                    methode: result.method,
                    isMobile: result.isMobile
                });
                setCountry(result.country);
                setDetectionMethod(result.method);
                setIsMobile(result.isMobile);

                if (result.country === 'BJ' && result.method === 'Valeur par défaut') {
                    setDetectionError('Détection automatique impossible, utilisation du Bénin par défaut');
                    console.warn('⚠️ [Hook] Utilisation de la valeur par défaut (Bénin)');
                } else {
                    console.log(`🎉 [Hook] Pays détecté avec succès: ${result.country} (${result.method})`);
                }
            } catch (error) {
                console.error('❌ [Hook] Échec complet de la détection:', error);
                setDetectionError('Impossible de détecter votre pays automatiquement');
                setCountry('BJ');
                setDetectionMethod('Valeur par défaut');
            } finally {
                setIsDetecting(false);
                console.log('🏁 [Hook] Détection terminée');
            }
        };

        detectCountry();
    }, []);

    return { country, isDetecting, detectionMethod, isMobile, detectionError, reDetect };
};
// ========== FIN DES FONCTIONS ==========

const DevenirPartenaire = () => {
    console.log('📄 [Page] Chargement de DevenirPartenaire');
    const location = useLocation();
    const navigate = useNavigate();
    const user_id = localStorage.getItem('user_id');
    console.log('👤 [Page] user_id:', user_id);

    // Détection de pays avec la fonction reDetect
    const { country: detectedCountry, isDetecting: isDetectingCountry, detectionMethod, isMobile, detectionError, reDetect } = useCountryDetection();

    const [formData, setFormData] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });
    const [codeError, setCodeError] = useState('');
    const [generatedCode, setGeneratedCode] = useState('');
    const [selectedCountry, setSelectedCountry] = useState('BJ');
    const [showRetryButton, setShowRetryButton] = useState(false);

    // NOUVEAU : États pour le modal de convention
    const [showModal, setShowModal] = useState(false);
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [tempFormData, setTempFormData] = useState(null);

    // Mettre à jour le pays détecté
    useEffect(() => {
        if (detectedCountry && !isDetectingCountry) {
            console.log(`🔄 [Mise à jour] Pays détecté mis à jour: ${detectedCountry} (${detectionMethod})`);
            setSelectedCountry(detectedCountry);
            if (detectionMethod === 'Valeur par défaut') {
                setShowRetryButton(true);
                console.log('⚠️ [Mise à jour] Affichage du bouton de réessai');
            } else {
                setShowRetryButton(false);
            }
        }
    }, [detectedCountry, isDetectingCountry, detectionMethod]);

    // Fonction pour re-détecter le pays
    const handleRedetectCountry = async () => {
        console.log('🔄 [UI] Clic sur le bouton Réessayer');
        const result = await reDetect();
        if (result && result.country) {
            setSelectedCountry(result.country);
            console.log(`✅ [UI] Nouveau pays détecté: ${result.country}`);
            if (result.country === 'BJ' && result.method === 'Valeur par défaut') {
                setShowRetryButton(true);
                showAlert('Toujours impossible de détecter votre pays automatiquement', 'warning');
            } else {
                setShowRetryButton(false);
                showAlert(`Pays détecté avec succès: ${result.country}`, 'success');
            }
        } else {
            console.error('❌ [UI] Échec de la re-détection');
            showAlert('Erreur lors de la détection du pays', 'danger');
        }
    };

    // Fonction pour générer un code aléatoire au format IVTBJ-XX-1234
    const generateRandomCode = () => {
        const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        const randomLetters = letters.charAt(Math.floor(Math.random() * letters.length)) +
            letters.charAt(Math.floor(Math.random() * letters.length));
        const randomNumbers = Math.floor(1000 + Math.random() * 9000);
        return `IVTBJ-${randomLetters}-${randomNumbers}`;
    };

    const validateCodePartenaire = (code) => {
        const regex = /^IVTBJ-[A-Z]{2}-\d{4}$/;
        return regex.test(code);
    };

    const handleCodeChange = (value) => {
        const upperValue = value.toUpperCase();
        setGeneratedCode(upperValue);
        if (upperValue && !validateCodePartenaire(upperValue)) {
            setCodeError('Format invalide. Exemple: IVTBJ-AZ-1234');
        } else {
            setCodeError('');
        }
    };

    const TextAvecVoirPlus = () => {
        const [voirPlus, setVoirPlus] = useState(false);

        const texteComplet = `Dans le cadre de ses prestations, INOVACT procède au traitement de données personnelles intéressant 
      le Client ainsi que des participants aux sessions de formations tels que figurant dans le bulletin d’inscription. 
      En tout état de cause INOVACT ne collecte des informations personnelles relatives à l’utilisateur que pour le 
      besoin des services proposés. L’utilisateur fournit ces informations en toute connaissance de cause, 
      notamment lorsqu’il procède par lui-même à leur saisie. Conformément aux dispositions de la loi 2017-20 portant 
      code du numérique en République du Bénin, tout utilisateur dispose d’un droit d’accès, de rectification et d’opposition 
      aux données personnelles le concernant, en effectuant sa demande écrite et signée, accompagnée d’une copie du titre 
      d’identité avec signature du titulaire de la pièce, en précisant l’adresse à laquelle la réponse doit être envoyée. 
      Aucune information personnelle de l’utilisateur n’est publiée à l’insu de l’utilisateur, échangée, transférée, 
      cédée ou vendue sur un support quelconque à des tiers. Les données à caractère personnel collectées seront traitées 
      exclusivement à des fins d’exécution des présentes conditions de vente et également en vue d’actions commerciales, 
      afin de proposer au Client ou aux participants qui y consentent des services analogues à ceux objets du présent 
      contrat par INOVACT. Les données à caractère personnel collectées peuvent être communiquées à des tiers auxquels 
      il serait nécessaire de les transmettre en exécution des prestations convenues et notamment aux organismes de financement, 
      afin de permettre la prise en charge des formations objets des présentes. Conformément à la loi précitée, 
      vous pouvez exercer vos droits d’accès, d’opposition, de rectification et de suppression auprès de la direction de INOVACT.`;

        const texteAbrege = `Dans le cadre de ses prestations, INOVACT procède au traitement de données personnelles intéressant 
      le Client ainsi que des participants aux sessions de formations. Conformément à la loi 2017-20 portant code du numérique 
      en République du Bénin, tout utilisateur dispose d'un droit d'accès, de rectification et d'opposition aux données 
      personnelles le concernant...`;

        return (
            <div className="text-justify mb-3">
                <small>
                    {voirPlus ? texteComplet : texteAbrege}
                    <button
                        type="button"
                        className="btn btn-link p-0 ml-1"
                        onClick={() => setVoirPlus(!voirPlus)}
                        style={{
                            fontSize: 'inherit',
                            textDecoration: 'underline',
                            border: 'none',
                            background: 'none',
                            color: '#007bff',
                            cursor: 'pointer'
                        }}
                    >
                        {voirPlus ? 'Voir moins' : 'Voir plus'}
                    </button>
                </small>
            </div>
        );
    };

    // NOUVEAU : Composant pour le texte de la convention
    const TexteConvention = () => {
        return (
            <div style={{ maxHeight: '600px', overflowY: 'auto', padding: '10px' }}>
                <h2 className="text-left mb-3" style={{ color: "#1c84c6", fontWeight: "bold", marginTop: 0 }}>CONVENTION DE PARTENARIAT</h2>

                <h3>Préambule</h3>
                <p>La présente convention a pour objet de définir les modalités de collaboration entre INOVACT et tout apporteur d'affaires dans le cadre de la recommandation de prospects et de clients pour les formations proposées par INOVACT.</p>

                <h3>1. Objet du partenariat</h3>
                <p>L'apporteur d'affaires met en relation INOVACT avec des prospects susceptibles de s'inscrire aux formations proposées par INOVACT.</p>
                <p>En contrepartie, l'apporteur d'affaires peut percevoir une commission sur les ventes effectivement réalisées, dans les conditions prévues par la présente convention.</p>

                <h3>2. Identification du client apporté</h3>
                <p>Est considéré comme client apporté tout client s'étant inscrit auprès d'INOVACT au moyen du code de recommandation attribué à l'apporteur d'affaires ou ayant été identifié comme tel dans le système de suivi d'INOVACT.</p>
                <p>Les informations enregistrées sur la plateforme d'INOVACT font foi pour l'identification du client apporté.</p>

                <h3>3. Commission de l'apporteur d'affaires</h3>
                <p>Pour chaque client apporté et ayant souscrit à une offre de formation INOVACT, l'apporteur d'affaires bénéficie d'une commission maximale de dix pour cent (10%) du montant de la prestation.</p>
                <p>L'apporteur d'affaires peut choisir de céder tout ou partie de cette commission au client recommandé sous forme de remise commerciale.</p>
                <p>À titre indicatif :</p>
                <ul>
                    <li>si aucune remise n'est accordée au client, l'apporteur d'affaires perçoit la totalité de la commission de 10%;</li>
                    <li>si l'apporteur d'affaires accorde une remise de 5% au client, il conserve une commission de 5%;</li>
                    <li>toute remise consentie au client est automatiquement déduite de la commission attribuée à l'apporteur d'affaires.</li>
                </ul>
                <p>En aucun cas la somme de la commission de l'apporteur d'affaires et de la remise accordée au client ne peut excéder dix pour cent (10%) du montant de la prestation concernée.</p>

                <h3>4. Conditions de paiement des commissions</h3>
                <p>La commission est calculée sur le montant effectivement encaissé par INOVACT.</p>
                <p>Elle devient exigible uniquement après règlement intégral des frais de formation par le client recommandé.</p>
                <p>Aucune commission ne pourra être versée dans les cas suivants :</p>
                <ul>
                    <li>paiement partiel du client;</li>
                    <li>annulation de l'inscription;</li>
                    <li>désistement du client;</li>
                    <li>non-paiement ou impayé.</li>
                </ul>
                <p>Le versement de la commission intervient après validation du paiement complet du client et réception d'une facture normalisée émise par l'apporteur d'affaires conformément à la réglementation en vigueur.</p>

                <h3>5. Suivi des commissions</h3>
                <p>INOVACT met à la disposition de l'apporteur d'affaires un espace personnel lui permettant de consulter :</p>
                <ul>
                    <li>les clients recommandés;</li>
                    <li>les commissions générées;</li>
                    <li>les commissions payées;</li>
                    <li>l'historique de ses opérations.</li>
                </ul>
                <p>Les informations affichées sur la plateforme font foi pour le suivi des commissions.</p>

                <h3>6. Absence de lien de subordination</h3>
                <p>La présente convention ne crée aucun lien de subordination, de représentation commerciale exclusive ou de relation salariale entre INOVACT et l'apporteur d'affaires.</p>
                <p>L'apporteur d'affaires exerce son activité en toute indépendance et demeure seul responsable de ses obligations fiscales, sociales et administratives.</p>

                <h3>7. Acceptation de la convention</h3>
                <p>En adhérant au programme de partenariat et en validant son inscription sur la plateforme, l'apporteur d'affaires reconnaît avoir pris connaissance de la présente convention et en accepter l'intégralité des dispositions sans réserve.</p>
            </div>
        );
    };

    const fetchUtilisateur = async () => {
        console.log('📡 [API] Récupération des données utilisateur...');
        try {
            const token = localStorage.getItem('access_token');

            const response = await fetch(`${process.env.REACT_APP_HOTE}/recup-info-profil/${user_id}/`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });

            const data = await response.json();
            console.log('✅ [API] Données utilisateur récupérées:', data);
            setFormData(data);
            if (data.code_partenaire) {
                setGeneratedCode(data.code_partenaire);
            }
        } catch (error) {
            console.error('❌ [API] Erreur lors de la récupération des données utilisateur', error);
        }
    };

    useEffect(() => {
        if (user_id) {
            fetchUtilisateur();
        }
    }, [user_id]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handlePhoneChange = (value) => {
        console.log('📞 [Téléphone] Changement de numéro:', value);
        setFormData({
            ...formData,
            tel: value,
        });
    };

    const pageStyle = {
        backgroundColor: '#f5f7fa',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    };

    const showAlert = (message, type = 'success') => {
        console.log(`🔔 [Alerte] ${type}: ${message}`);
        setAlert({ show: true, message, type });
        setTimeout(() => setAlert({ show: false, message: '', type: '' }), 5000);
    };

    // NOUVEAU : Fonction pour ouvrir le modal (remplace l'envoi direct)
    const handleOpenModal = (e) => {
        e.preventDefault();

        // Vérifier le code partenaire avant d'ouvrir le modal
        if (!generatedCode || !validateCodePartenaire(generatedCode)) {
            showAlert('Veuillez saisir un code partenaire valide au format IVTBJ-XX-1234', 'danger');
            return;
        }

        // Sauvegarder temporairement les données
        setTempFormData({
            ...formData,
            user_id,
            code_partenaire: generatedCode,
        });

        setAcceptTerms(false);
        setShowModal(true);
    };

    // NOUVEAU : Fonction pour confirmer et envoyer l'enregistrement
    const handleConfirmAndSubmit = async () => {
        if (!acceptTerms) {
            showAlert('Veuillez accepter les termes de la convention de partenariat', 'warning');
            return;
        }

        setShowModal(false);
        setIsLoading(true);

        try {
            const response = await fetch(process.env.REACT_APP_HOTE + `/enregistrer-partenaire/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(tempFormData),
            });

            const result = await response.json();

            if (response.ok) {
                showAlert(result.message, 'success');
                localStorage.setItem('user_id', result.utilisateur.id);
                navigate(`/dashboard`);
            } else {
                showAlert(result.error, 'danger');
                if (result.error && result.error.includes('existe déjà')) {
                    setGeneratedCode(generateRandomCode());
                }
            }
        } catch (error) {
            console.error('Error:', error);
            showAlert('Une erreur est survenue', 'danger');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRetour = () => {
        console.log('🔙 [Navigation] Retour à l\'accueil');
        navigate(`/`);
    };

    const handleGenerateNewCode = () => {
        console.log('🎲 [Code] Génération nouveau code partenaire');
        setGeneratedCode(generateRandomCode());
        setCodeError('');
    };

    return (
        <div style={pageStyle}>
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-md-10 col-lg-8">
                        <div className="loginscreen animated fadeInDown">
                            <div className="ibox-content p-4 rounded">
                                <div className="text-center mb-4">
                                    <FaHandshake size={25} />
                                    <h3 className="font-bold">Devenir partenaire de INOVACT Académie </h3>
                                </div>
                                {alert.show && (
                                    <Alert variant={alert.type} onClose={() => setAlert({ show: false })}>
                                        {alert.message}
                                    </Alert>
                                )}

                                <form className="m-t" role="form" onSubmit={handleOpenModal}>
                                    <div className="form-row">
                                        <div className="form-group text-left col-md-4">
                                            <label>Civilité <span className="text-danger">*</span></label>
                                            <select
                                                className="form-control"
                                                required
                                                name="civilite"
                                                value={formData.civilite}
                                                onChange={handleInputChange}
                                            >
                                                <option value="">Sélectionnez</option>
                                                <option value="Monsieur">Monsieur</option>
                                                <option value="Madame">Madame</option>
                                                <option value="Mademoiselle">Mademoiselle</option>
                                            </select>
                                        </div>
                                        <div className="form-group text-left col-md-4">
                                            <label>Nom <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Nom"
                                                required
                                                name="nom"
                                                value={formData.nom}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                        <div className="form-group text-left col-md-4">
                                            <label>Prénom(s) <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Prénom"
                                                required
                                                name="prenoms"
                                                value={formData.prenoms}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group text-left col-md-4">
                                            <label>Email <span className="text-danger">*</span></label>
                                            <input
                                                type="email"
                                                className="form-control"
                                                placeholder="Email"
                                                required
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                        <div className="form-group text-left col-md-4">
                                            <label>Téléphone <span className="text-danger">*</span></label>
                                            <PhoneInput
                                                international
                                                defaultCountry={selectedCountry}
                                                value={formData.tel}
                                                onChange={handlePhoneChange}
                                                className="w-100"
                                                required
                                            />
                                        </div>
                                        <div className="form-group text-left col-md-4">
                                            <label>Type de profil <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                className="form-control bg-light"
                                                value="Partenaire"
                                                readOnly
                                                disabled
                                            />
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group text-left col-md-6">
                                            <label>Nom de l'organisation </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Nom de l'organisation"
                                                required
                                                name="nom_organisation"
                                                value={formData.nom_organisation}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                        <div className="form-group text-left col-md-6">
                                            <label>Email de l'organisation </label>
                                            <input
                                                type="email"
                                                className="form-control"
                                                placeholder="Email de l'organisation"
                                                required
                                                name="email_corporate"
                                                value={formData.email_corporate}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group text-left col-md-12">
                                            <label>
                                                Code partenaire <span className="text-danger">*</span>
                                                <small className="text-muted ml-2">
                                                    (Format: IVTBJ-XX-1234)
                                                </small>
                                            </label>
                                            <div className="input-group">
                                                <div className="input-group-prepend">
                                                    <span className="input-group-text">
                                                        <FaTag />
                                                    </span>
                                                </div>
                                                <input
                                                    type="text"
                                                    className={`form-control ${codeError ? 'is-invalid' : ''}`}
                                                    placeholder="Saisir code partenaire (ex : IVTBJ-AZ-1234) ou générer"
                                                    value={generatedCode}
                                                    onChange={(e) => handleCodeChange(e.target.value)}
                                                />
                                                <div className="input-group-append">
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary"
                                                        onClick={handleGenerateNewCode}
                                                        title="Générer un nouveau code"
                                                    >
                                                        <i className="fa fa-random"></i> Générer
                                                    </button>
                                                </div>
                                            </div>
                                            {codeError && (
                                                <div className="invalid-feedback d-block">
                                                    {codeError}
                                                </div>
                                            )}
                                            <small className="form-text text-muted">
                                                Ce code vous permet de bénéficier d’une commission.
                                            </small>
                                        </div>
                                    </div>
                                </form>
                                <div className='d-flex justify-content-between'>
                                    <button
                                        onClick={handleRetour}
                                        type="submit"
                                        className="btn btn-secondary block full-width m-1"
                                    >
                                        Retour
                                    </button>
                                    <Button
                                        variant="success"
                                        onClick={handleOpenModal}
                                        disabled={isLoading}
                                        className="btn btn-secondary block full-width m-1"
                                    >
                                        {isLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                                Enregistrement...
                                            </>
                                        ) : (
                                            <>
                                                <FaSave className="me-1" /> Enregistrer
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {/* NOUVEAU : Modal de convention de partenariat */}
                        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
                            {/* <Modal.Header closeButton style={{ backgroundColor: '#1c84c6', color: 'white' }}>
                                <Modal.Title>
                                    <FaFileContract className="mr-2" /> Convention de partenariat INOVACT
                                </Modal.Title>
                            </Modal.Header> */}
                            <Modal.Body>
                                <TexteConvention />
                                <hr />
                                <div className="form-check mt-3">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="acceptTerms"
                                        checked={acceptTerms}
                                        onChange={(e) => setAcceptTerms(e.target.checked)}
                                        style={{ cursor: 'pointer' }}
                                    />
                                    {/* <label className="form-check-label" htmlFor="acceptTerms" style={{ cursor: 'pointer' }}>
                                        <strong>J'ai lu et j'accepte les termes de la Convention de partenariat d'INOVACT</strong>
                                    </label> */}
                                </div>
                                <div className="form-check mt-2">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="acceptTerms2"
                                        checked={acceptTerms}
                                        onChange={(e) => setAcceptTerms(e.target.checked)}
                                        style={{ cursor: 'pointer' }}
                                    />
                                    <label className="form-check-label" htmlFor="acceptTerms2" style={{ cursor: 'pointer' }}>
                                        <strong>Je reconnais avoir lu et accepté la Convention de partenariat d'INOVACT et m'engage à en respecter l'ensemble des dispositions</strong>
                                    </label>
                                </div>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={() => setShowModal(false)}>
                                    Annuler
                                </Button>
                                <Button
                                    variant="success"
                                    onClick={handleConfirmAndSubmit}
                                    disabled={!acceptTerms}
                                >
                                    <FaCheckCircle className="mr-1" /> Je confirme mon engagement
                                </Button>
                            </Modal.Footer>
                        </Modal>

                        <div
                            className="ibox-footer full-w mt-2 d-flex align-items-center justify-content-between pl-3 pr-3"
                            style={{
                                textAlign: 'center',
                                width: '100%',
                            }}
                        >
                            <small>© {new Date().getFullYear()} <strong>Inovact</strong>. Tous droits réservés.</small>

                            <div className='text-left mb-2 ml-1'>
                                <a className='text-left text-success' style={{
                                    textDecoration: 'underline',
                                    textDecorationColor: '#1c09ecff',
                                    transition: 'all 0.3s ease'
                                }}
                                    href='https://inovact.com/politique-confidentialite'
                                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline dotted'}
                                    onMouseLeave={(e) => e.target.style.textDecoration = 'underline'}>
                                    Politique de confidentialité
                                </a>
                            </div>

                            <div className="d-flex align-items-center justify-content-between">
                                <small className="me-3 text-muted mr-2">Nous suivre</small>
                                <div className="d-flex align-items-center gap-3">
                                    <a href="https://www.facebook.com/inovact/" className="text-decoration-none social-icon d-flex align-items-center justify-content-center mr-2"
                                        style={{
                                            width: '20px',
                                            height: '20px',
                                            borderRadius: '50%',
                                            backgroundColor: '#4c4d4e10',
                                            border: '1px solid #4c4d4e10',
                                            transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#6c757d';
                                            e.currentTarget.style.borderColor = '#6c757d';
                                            e.currentTarget.querySelector('i').style.color = 'white';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = '#4c4d4e10';
                                            e.currentTarget.style.borderColor = '#4c4d4e10';
                                            e.currentTarget.querySelector('i').style.color = '#6c757d';
                                        }}
                                        title="Facebook">
                                        <i className="fa fa-facebook-f" style={{ fontSize: '12px', color: '#6c757d', transition: 'color 0.3s ease' }}></i>
                                    </a>
                                    <a href="https://x.com/Inovact_Sarl" className="text-decoration-none social-icon d-flex align-items-center justify-content-center mr-2"
                                        style={{
                                            width: '20px',
                                            height: '20px',
                                            borderRadius: '50%',
                                            backgroundColor: '#4c4d4e10',
                                            border: '1px solid #4c4d4e10',
                                            transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#6c757d';
                                            e.currentTarget.style.borderColor = '#6c757d';
                                            e.currentTarget.querySelector('i').style.color = 'white';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = '#4c4d4e10';
                                            e.currentTarget.style.borderColor = '#4c4d4e10';
                                            e.currentTarget.querySelector('i').style.color = '#6c757d';
                                        }}
                                        title="Twitter">
                                        <i className="fa fa-twitter" style={{ fontSize: '12px', color: '#6c757d', transition: 'color 0.3s ease' }}></i>
                                    </a>
                                    <a href="https://www.linkedin.com/company/inovact" className="text-decoration-none social-icon d-flex align-items-center justify-content-center mr-2"
                                        style={{
                                            width: '20px',
                                            height: '20px',
                                            borderRadius: '50%',
                                            backgroundColor: '#4c4d4e10',
                                            border: '1px solid #4c4d4e10',
                                            transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#6c757d';
                                            e.currentTarget.style.borderColor = '#6c757d';
                                            e.currentTarget.querySelector('i').style.color = 'white';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = '#4c4d4e10';
                                            e.currentTarget.style.borderColor = '#4c4d4e10';
                                            e.currentTarget.querySelector('i').style.color = '#6c757d';
                                        }}
                                        title="LinkedIn">
                                        <i className="fa fa-linkedin" style={{ fontSize: '12px', color: '#6c757d', transition: 'color 0.3s ease' }}></i>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DevenirPartenaire;